import React from "react";

/**
 * 2026-10-27: Åbningstider pr. ugedag på et sted (kundens adresse, modtageanlæg, egen lokation).
 * Ved egne ture og fragt er det stedet - ikke et materiale - der har åbningstiderne, og fx
 * "lukket i weekenden og på helligdage" kan ikke skrives med ét tidsrum.
 * Format: { mon: { closed, from, to }, ..., sun, holidays_closed }.
 */
export const WEEK = [["mon", "Man"], ["tue", "Tir"], ["wed", "Ons"], ["thu", "Tor"], ["fri", "Fre"], ["sat", "Lør"], ["sun", "Søn"]];
const LONG = { mon: "mandag", tue: "tirsdag", wed: "onsdag", thu: "torsdag", fri: "fredag", sat: "lørdag", sun: "søndag" };
const START = { ...Object.fromEntries(WEEK.map(([k]) => [k, ["sat", "sun"].includes(k) ? { closed: true, from: "", to: "" } : { closed: false, from: "07:00", to: "16:00" }])),
  holidays_closed: true };

export function dayKey(dateStr) {
  if (!dateStr) return "";
  return ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][new Date(`${dateStr}T12:00:00`).getDay()];
}

/** 2026-10-28: Et ældre sted med ét tidsrum (open_from/open_to) som det samme tidsrum alle dage -
 *  så formularerne kun har én måde at skrive tider på. */
export function weekFromRange(place) {
  if (!place) return null;
  if (place.weekly_hours) return place.weekly_hours;
  if (!place.open_from || !place.open_to) return null;
  return { ...Object.fromEntries(WEEK.map(([k]) => [k, { closed: false, from: place.open_from, to: place.open_to }])), holidays_closed: false };
}

/** Stedets tider den dag: { closed, open_from, open_to, why } - eller null uden tider. */
export function hoursOn(place, dateStr, holidays = {}) {
  if (!place || !dateStr) return null;
  const w = place.weekly_hours;
  if (w) {
    if (w.holidays_closed && holidays[dateStr]) return { closed: true, why: holidays[dateStr] };
    const k = dayKey(dateStr);
    const d = w[k] || {};
    if (d.closed) return { closed: true, why: LONG[k] };
    return { closed: false, open_from: d.from || "", open_to: d.to || "" };
  }
  return place.open_from && place.open_to ? { closed: false, open_from: place.open_from, open_to: place.open_to } : null;
}

/** Kort tekst til lister: "Man–fre 07–16 · lør–søn lukket · lukket på helligdage". */
export function weekSummary(w) {
  if (!w) return "";
  const txt = (d) => (d?.closed ? "lukket" : d?.from && d?.to ? `${d.from}–${d.to}` : "-");
  const parts = [];
  let i = 0;
  while (i < WEEK.length) {
    let j = i;
    while (j + 1 < WEEK.length && txt(w[WEEK[j + 1][0]]) === txt(w[WEEK[i][0]])) j += 1;
    const label = i === j ? WEEK[i][1] : `${WEEK[i][1]}–${WEEK[j][1].toLowerCase()}`;
    parts.push(`${label} ${txt(w[WEEK[i][0]])}`);
    i = j + 1;
  }
  return [...parts, w.holidays_closed ? "lukket på helligdage" : ""].filter(Boolean).join(" · ");
}

const inp = "px-2 py-1 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm w-[5.5rem]";

export default function WeeklyHours({ value, onChange, testid = "weekly-hours" }) {
  if (!value) {
    return (
      <button type="button" className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 underline" onClick={() => onChange({ ...START })}
        data-testid={`${testid}-add`}>
        + Åbningstider pr. ugedag (fx lukket i weekenden og på helligdage)
      </button>
    );
  }
  const setDay = (k, patch) => onChange({ ...value, [k]: { ...(value[k] || {}), ...patch } });
  const copyMon = () => onChange({ ...value, ...Object.fromEntries(["tue", "wed", "thu", "fri"].map((k) => [k, { ...value.mon }])) });
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5 space-y-1.5" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold text-slate-600 dark:text-slate-300">Åbningstider pr. ugedag</span>
        <span className="flex gap-3">
          <button type="button" className="text-[11px] underline text-slate-500" onClick={copyMon}>Mandag på alle hverdage</button>
          <button type="button" className="text-[11px] underline text-slate-500" onClick={() => onChange(null)} data-testid={`${testid}-clear`}>Fjern</button>
        </span>
      </div>
      {WEEK.map(([k, l]) => {
        const d = value[k] || {};
        return (
          <div key={k} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="w-9 font-semibold">{l}</span>
            <label className="inline-flex items-center gap-1 text-xs"><input type="checkbox" checked={!!d.closed} onChange={(e) => setDay(k, { closed: e.target.checked })}
              data-testid={`${testid}-${k}-closed`} />Lukket</label>
            {!d.closed && <>
              <input type="time" className={inp} value={d.from || ""} onChange={(e) => setDay(k, { from: e.target.value })} data-testid={`${testid}-${k}-from`} />
              <span className="text-slate-400">–</span>
              <input type="time" className={inp} value={d.to || ""} onChange={(e) => setDay(k, { to: e.target.value })} data-testid={`${testid}-${k}-to`} />
            </>}
          </div>
        );
      })}
      <label className="inline-flex items-center gap-1.5 text-xs pt-1"><input type="checkbox" checked={!!value.holidays_closed}
        onChange={(e) => onChange({ ...value, holidays_closed: e.target.checked })} data-testid={`${testid}-holidays`} />Lukket på helligdage</label>
    </div>
  );
}
